import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
// Lib
import { apiClient } from "@/lib/apiClient";
// UI
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
// Icons
import { CommunicationFeedback, Email, Tickets } from "@/components/icons";
// Components
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";
// Hooks
import { useReportData } from "../../hooks/useReportData";
// Styles
import styles from "./CommunicationEffectivenessReportPage.module.scss";

// ---------------------------------------------------------------------------
// API shape (local — backend hazır, route: /api/admin/reports/communication-effectiveness)
// ---------------------------------------------------------------------------

type CommunicationChannel = "EMAIL" | "SMS" | "ANNOUNCEMENT";

export interface CommunicationCampaignItem {
  campaignID: string;
  channel: CommunicationChannel;
  subject: string;
  recipients: number;
  delivered: number;
  failed: number;
  pending: number;
  deliveryRatePercent: number;
  sentAt: string | null;
  createdAt: string;
}

export interface CommunicationMonthlyPoint {
  month: string;
  email: number;
  sms: number;
  announcement: number;
}

export interface CommunicationBreakdown {
  label: string;
  count: number;
}

export interface CommunicationResponse {
  summary: {
    totalCampaigns: number;
    totalRecipients: number;
    emailDeliveryRatePercent: number;
    smsDeliveryRatePercent: number;
    announcementReadRatePercent: number;
    avgRecipientsPerCampaign: number;
  };
  byChannel: CommunicationBreakdown[];
  byStatus: CommunicationBreakdown[];
  monthlyTrend: CommunicationMonthlyPoint[];
  items: CommunicationCampaignItem[];
  count: number;
}

export interface CommunicationParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

const PAGE_SIZE = 20;
const ENDPOINT = "/api/admin/reports/communication-effectiveness";

/** TransformResponseInterceptor `{ data: <payload> }` döner — güvenli unwrap. */
function unwrap<T>(body: { data?: T } | T): T {
  if (body && typeof body === "object" && "data" in (body as object)) {
    const d = (body as { data?: T }).data;
    if (d !== undefined) return d;
  }
  return body as T;
}

async function fetchCommunication(
  params: CommunicationParams,
  signal: AbortSignal,
): Promise<CommunicationResponse> {
  const res = await apiClient.get<CommunicationResponse>(ENDPOINT, {
    params,
    signal,
  });
  return unwrap<CommunicationResponse>(
    res.data as unknown as { data?: CommunicationResponse } | CommunicationResponse,
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const numberFmt = new Intl.NumberFormat("tr-TR");
const formatNumber = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : numberFmt.format(v);

const formatPercent = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${v.toFixed(1)}%`;

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const channelLabel = (c: CommunicationChannel, t: (k: string) => string) => {
  switch (c) {
    case "EMAIL":
      return t("reports.communicationEffectiveness.channels.email");
    case "SMS":
      return t("reports.communicationEffectiveness.channels.sms");
    case "ANNOUNCEMENT":
      return t("reports.communicationEffectiveness.channels.announcement");
    default:
      return c;
  }
};

const channelClass = (c: CommunicationChannel) => {
  switch (c) {
    case "EMAIL":
      return styles.channelEmail;
    case "SMS":
      return styles.channelSms;
    case "ANNOUNCEMENT":
      return styles.channelAnnouncement;
    default:
      return "";
  }
};

const rateClass = (rate: number) => {
  if (rate >= 90) return styles.rateGood;
  if (rate >= 70) return styles.rateWarn;
  return styles.rateBad;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const CommunicationEffectivenessReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: CommunicationParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const fetcher = useCallback(
    (p: CommunicationParams, signal: AbortSignal) => fetchCommunication(p, signal),
    [],
  );

  const { data, isLoading, error } = useReportData<
    CommunicationResponse,
    CommunicationParams
  >({
    fetcher,
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const monthly = data?.monthlyTrend ?? [];
  const byChannel = data?.byChannel ?? [];
  const byStatus = data?.byStatus ?? [];

  const monthlySeries = useMemo(
    () => [
      {
        name: t("reports.communicationEffectiveness.channels.email"),
        data: monthly.map((m) => m.email),
      },
      {
        name: t("reports.communicationEffectiveness.channels.sms"),
        data: monthly.map((m) => m.sms),
      },
      {
        name: t("reports.communicationEffectiveness.channels.announcement"),
        data: monthly.map((m) => m.announcement),
      },
    ],
    [monthly, t],
  );

  const monthlyCategories = useMemo(() => monthly.map((m) => m.month), [monthly]);

  const channelDonutSeries = useMemo(
    () => byChannel.map((b) => b.count),
    [byChannel],
  );
  const channelDonutLabels = useMemo(
    () => byChannel.map((b) => b.label),
    [byChannel],
  );

  const statusDonutSeries = useMemo(
    () => byStatus.map((b) => b.count),
    [byStatus],
  );
  const statusDonutLabels = useMemo(
    () => byStatus.map((b) => b.label),
    [byStatus],
  );

  const columns: TableColumn<CommunicationCampaignItem>[] = useMemo(
    () => [
      {
        header: t("reports.communicationEffectiveness.headerChannel"),
        accessor: (row) => (
          <span className={`${styles.channelBadge} ${channelClass(row.channel)}`}>
            {channelLabel(row.channel, t)}
          </span>
        ),
      },
      {
        header: t("reports.communicationEffectiveness.headerSubject"),
        accessor: (row) => (
          <span className={styles.subjectCell} title={row.subject}>
            {row.subject || "—"}
          </span>
        ),
      },
      {
        header: t("reports.communicationEffectiveness.headerRecipients"),
        accessor: (row) => formatNumber(row.recipients),
      },
      {
        header: t("reports.communicationEffectiveness.headerDelivered"),
        accessor: (row) => formatNumber(row.delivered),
      },
      {
        header: t("reports.communicationEffectiveness.headerFailed"),
        accessor: (row) => formatNumber(row.failed),
      },
      {
        header: t("reports.communicationEffectiveness.headerPending"),
        accessor: (row) => formatNumber(row.pending),
      },
      {
        header: t("reports.communicationEffectiveness.headerDeliveryRate"),
        accessor: (row) => (
          <span className={rateClass(row.deliveryRatePercent)}>
            {formatPercent(row.deliveryRatePercent)}
          </span>
        ),
      },
      {
        header: t("reports.communicationEffectiveness.headerSentAt"),
        accessor: (row) => (
          <span className={row.sentAt ? undefined : styles.muted}>
            {formatDate(row.sentAt ?? row.createdAt)}
          </span>
        ),
      },
    ],
    [t],
  );

  return (
    <ReportDetailLayout
      title={t("reports.communicationEffectiveness.title")}
      categoryLabel={t("reports.communicationEffectiveness.category")}
      description={t("reports.communicationEffectiveness.description")}
      exportSlot={
        <ReportExportButtons
          reportType="COMMUNICATION_EFFECTIVENESS"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="iletisim-etkinligi"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDepartment={false}
        />
      }
    >
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.communicationEffectiveness.kpi.emailDelivery")}
          value={
            summary ? formatPercent(summary.emailDeliveryRatePercent) : "—"
          }
          icon={<Email width={18} height={18} />}
          accent={
            summary && summary.emailDeliveryRatePercent >= 90
              ? "success"
              : summary && summary.emailDeliveryRatePercent >= 70
                ? "warning"
                : "danger"
          }
        />
        <ReportKPICard
          title={t("reports.communicationEffectiveness.kpi.smsDelivery")}
          value={
            summary ? formatPercent(summary.smsDeliveryRatePercent) : "—"
          }
          icon={<CommunicationFeedback width={18} height={18} />}
          accent={
            summary && summary.smsDeliveryRatePercent >= 90
              ? "success"
              : summary && summary.smsDeliveryRatePercent >= 70
                ? "warning"
                : "danger"
          }
        />
        <ReportKPICard
          title={t("reports.communicationEffectiveness.kpi.announcementRead")}
          value={
            summary
              ? formatPercent(summary.announcementReadRatePercent)
              : "—"
          }
          icon={<Tickets width={18} height={18} />}
          accent={
            summary && summary.announcementReadRatePercent >= 70
              ? "success"
              : summary && summary.announcementReadRatePercent >= 40
                ? "warning"
                : "default"
          }
        />
        <ReportKPICard
          title={t("reports.communicationEffectiveness.kpi.totalCampaigns")}
          value={summary ? formatNumber(summary.totalCampaigns) : "—"}
          icon={<CommunicationFeedback width={18} height={18} />}
          footnote={
            summary
              ? `${formatNumber(summary.avgRecipientsPerCampaign)} ${t("reports.communicationEffectiveness.kpi.avgPerCampaign")}`
              : undefined
          }
        />
      </div>

      <div className={styles.chartGrid}>
        <ReportChart
          title={t("reports.communicationEffectiveness.charts.monthlyTrend")}
          subtitle={t(
            "reports.communicationEffectiveness.charts.monthlyTrendSubtitle",
          )}
          type="line"
          isLoading={isLoading}
          isEmpty={monthly.length === 0}
          height={300}
          series={monthlySeries}
          options={{
            xaxis: { categories: monthlyCategories },
            stroke: { curve: "smooth", width: 3 },
            markers: { size: 4 },
            colors: ["#4f46e5", "#f59e0b", "#10b981"],
            yaxis: {
              labels: {
                formatter: (val: number) => formatNumber(val),
              },
            },
          }}
        />

        <ReportChart
          title={t("reports.communicationEffectiveness.charts.channelMix")}
          type="donut"
          isLoading={isLoading}
          isEmpty={channelDonutSeries.length === 0}
          height={300}
          series={channelDonutSeries}
          options={{
            labels: channelDonutLabels,
            colors: ["#4f46e5", "#f59e0b", "#10b981"],
            legend: { position: "bottom" },
            plotOptions: {
              pie: {
                donut: {
                  size: "62%",
                },
              },
            },
          }}
        />

        <ReportChart
          title={t("reports.communicationEffectiveness.charts.statusMix")}
          type="donut"
          isLoading={isLoading}
          isEmpty={statusDonutSeries.length === 0}
          height={300}
          series={statusDonutSeries}
          options={{
            labels: statusDonutLabels,
            colors: ["#10b981", "#ef4444", "#f59e0b", "#94a3b8"],
            legend: { position: "bottom" },
            plotOptions: {
              pie: {
                donut: {
                  size: "62%",
                },
              },
            },
          }}
        />
      </div>

      <Table
        columns={columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.campaignID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.communicationEffectiveness.noData")
            : t("reports.communicationEffectiveness.empty")
        }
      />
    </ReportDetailLayout>
  );
};

export default CommunicationEffectivenessReportPage;
